import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { LegalFooter } from "./Legal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [vat, setVat] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [businessType, setBusinessType] = useState("libero_professionista");
  const [taxRegime, setTaxRegime] = useState("forfettario");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("La password deve contenere almeno 8 caratteri");
    if (!accept) return setError("Devi accettare Termini, Privacy e DPA per registrarti");
    setLoading(true);
    const res = await register(name || businessName || ownerName, email, password, accept, {
      business_name: businessName, owner_name: ownerName, vat_number: vat, address, phone,
      business_type: businessType, tax_regime: taxRegime,
    });
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error || "Registrazione non riuscita");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12 relative overflow-hidden">
        <div className="relative flex items-center gap-2">
          <BrandLogo size="lg" variant="mark" />
          <span className="font-extrabold text-xl tracking-tight">MyQuoteFlow</span>
        </div>
        <div className="relative">
          <h2 className="text-3xl font-extrabold leading-tight">Crea preventivi che si vendono da soli.</h2>
          <p className="text-slate-300 mt-4 max-w-sm">Registrati gratis e inizia subito: motore fiscale italiano, firma elettronica e incasso acconto inclusi.</p>
        </div>
        <div className="relative text-sm text-slate-400">© {new Date().getFullYear()} MyQuoteFlow</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-card">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <BrandLogo />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Crea il tuo account</h1>
          <p className="text-sm text-slate-500 mt-1 mb-7">È gratis. Nessuna carta richiesta.</p>

          {error && <div data-testid="register-error" className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nome e Cognome</Label>
              <Input data-testid="register-owner-name-input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Es. Mario Rossi" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ragione Sociale / Studio</Label>
              <Input data-testid="register-name-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Es. Studio Rossi" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo di Attività</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger data-testid="register-business-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="libero_professionista">Libero Professionista o Freelance</SelectItem>
                    <SelectItem value="ditta_individuale">Ditta Individuale Artigiano o Commerciante</SelectItem>
                    <SelectItem value="societa">Società di Capitali o Persone</SelectItem>
                    <SelectItem value="senza_piva">Soggetto senza Partita IVA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Regime Fiscale</Label>
                <Select value={taxRegime} onValueChange={setTaxRegime}>
                  <SelectTrigger data-testid="register-tax-regime" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="forfettario">Regime Forfettario</SelectItem>
                    <SelectItem value="semplificato">Regime Semplificato</SelectItem>
                    <SelectItem value="ordinario">Regime Ordinario</SelectItem>
                    <SelectItem value="occasionale">Prestazione Occasionale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">P.IVA / Cod. Fiscale</Label>
                <Input data-testid="register-vat-input" value={vat} onChange={(e) => setVat(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Telefono</Label>
                <Input data-testid="register-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Indirizzo Sede</Label>
              <Input data-testid="register-address-input" value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@esempio.it" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
              <Input data-testid="register-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Almeno 8 caratteri" className="mt-1.5" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox data-testid="register-legal-checkbox" checked={accept} onCheckedChange={setAccept} className="mt-0.5" />
              <span className="text-sm text-slate-600 leading-snug">
                Accetto i <Link to="/terms" target="_blank" className="text-primary hover:underline">Termini di Servizio</Link>, la <Link to="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link> e la Nomina a Responsabile del Trattamento (<Link to="/dpa" target="_blank" className="text-primary hover:underline">DPA Art. 28 GDPR</Link>).
              </span>
            </label>
          </div>

          <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full h-11 font-semibold mt-6">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea account gratis"}
          </Button>
          <p className="text-sm text-slate-500 text-center mt-5">
            Hai già un account? <Link to="/login" data-testid="register-to-login-link" className="text-primary font-medium hover:underline">Accedi</Link>
          </p>
        </form>
        <LegalFooter className="mt-10" />
      </div>
    </div>
  );
}
